import React, { useEffect, useRef, useState } from "react"
import { Row, Col, Tooltip, Progress, Button, Modal, message } from "antd"
import { InfoCircleOutlined } from "@ant-design/icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faCalendar,
  faClock,
  faRulerHorizontal,
  faBadgeSheriff,
  faUsers,
  faUser,
  faWaterLadder,
  faRoad,
  faLocationDot,
  faHouse,
  faPenToSquare,
} from "@fortawesome/pro-regular-svg-icons"

import { swimminglyApi, gen } from "./utils"
import type { IClubSeasonInformation } from "./MeetActionsModal"
import type { IClub } from "./utils"

interface MeetPoolLike {
  poolId: number | null
  pool_name: string | null
  description: string | null
  address: string | null
  numLanes: number | null
}

interface MeetOverviewTabProps {
  isVirtualMeet: boolean
  dateLabel: string
  formattedMeetTime: string | null
  dateTimeOverviewContent?: React.ReactNode
  meetEntriesDeadlineContent?: React.ReactNode
  deadlineStatusSummaryContent?: React.ReactNode
  courseLabel: string
  virtualCourseLabel?: string | null
  myMeetCourseLabel?: string | null
  individualEntryLimit: number | null
  relayEntryLimit: number | null
  totalEntryLimit: number | null
  scoringSummaryText: string | null
  numClubsInMeet: number
  virtualMeetId: number | null
  hostClub: IClub | null
  meetPool: MeetPoolLike | null
  meetLocation: string | null
  meetClubSeasons: IClubSeasonInformation[]
  homeClubId: number
  meetStatus: 0 | 1 | 2 | 3 | null
  meetId: number
  canEditMeetPool?: boolean
  onEditMeetPool?: () => void
  canEditHostClub?: boolean
  onEditHostClub?: () => void
  onHostClubChanged?: () => void
  canEditVirtualCourse?: boolean
  onEditVirtualCourse?: () => void
  canEditMyMeetCourse?: boolean
  onEditMyMeetCourse?: () => void
  entryLimitsOverviewContent?: React.ReactNode
  seasonEnded?: boolean
  renderHeatSheetTrackerAction?: (
    clubSeason: IClubSeasonInformation,
  ) => {
    onClick: () => void
    loading: boolean
  } | null
  meetDivisions?: {
    divisionId: number
    divisionNumber: number
    divisionName: string
    clubIds: number[]
  }[]
}

const MeetOverviewTab: React.FC<MeetOverviewTabProps> = ({
  isVirtualMeet,
  dateLabel,
  formattedMeetTime,
  dateTimeOverviewContent,
  meetEntriesDeadlineContent,
  deadlineStatusSummaryContent,
  courseLabel,
  virtualCourseLabel = null,
  myMeetCourseLabel = null,
  individualEntryLimit,
  relayEntryLimit,
  totalEntryLimit,
  scoringSummaryText,
  numClubsInMeet,
  virtualMeetId,
  hostClub,
  meetPool,
  meetLocation,
  meetClubSeasons,
  homeClubId,
  meetStatus,
  meetId,
  canEditMeetPool = false,
  onEditMeetPool,
  canEditHostClub = false,
  onEditHostClub,
  onHostClubChanged,
  canEditVirtualCourse = false,
  onEditVirtualCourse,
  canEditMyMeetCourse = false,
  onEditMyMeetCourse,
  entryLimitsOverviewContent,
  renderHeatSheetTrackerAction,
  seasonEnded = false,
  meetDivisions = [],
}) => {
  const [showHostTrigger, setShowHostTrigger] = useState(false)
  const [showPoolTrigger, setShowPoolTrigger] = useState(false)
  const [showVirtualCourseTrigger, setShowVirtualCourseTrigger] = useState(false)
  const [showMyMeetCourseTrigger, setShowMyMeetCourseTrigger] = useState(false)
  const [showVirtualCourseExplainer, setShowVirtualCourseExplainer] =
    useState(false)
  const [hoveredHostClubId, setHoveredHostClubId] = useState<number | null>(null)
  const [pendingHostClub, setPendingHostClub] = useState<IClub | null>(null)
  const [savingHost, setSavingHost] = useState(false)
  const hostAreaRef = useRef<HTMLDivElement | null>(null)
  const poolAreaRef = useRef<HTMLDivElement | null>(null)
  const virtualCourseAreaRef = useRef<HTMLDivElement | null>(null)
  const myMeetCourseAreaRef = useRef<HTMLDivElement | null>(null)

  const handleChangeHost = async () => {
    if (!pendingHostClub?.clubId) return
    setSavingHost(true)
    try {
      const result = await swimminglyApi
        .post(gen("/api/updateMeetHomeClub"))
        .body({ meetId, homeClubId: pendingHostClub.clubId })
      if (result?.status === "success") {
        message.success("Meet host updated.")
        setPendingHostClub(null)
        onHostClubChanged?.()
      } else {
        message.error(result?.message || "Failed to update meet host.")
      }
    } catch {
      message.error("Failed to update meet host.")
    } finally {
      setSavingHost(false)
    }
  }

  const formatLimit = (limit: number | null): string | null => {
    if (limit === null) return null
    if (limit >= 100) return "No limit"
    return String(limit)
  }

  const formattedIndividualLimit = formatLimit(individualEntryLimit)
  const formattedRelayLimit = formatLimit(relayEntryLimit)
  const formattedTotalLimit = formatLimit(totalEntryLimit)
  const hasAnyLimitText =
    formattedIndividualLimit !== null ||
    formattedRelayLimit !== null ||
    formattedTotalLimit !== null

  const localMeetClubSeasons = isVirtualMeet
    ? meetClubSeasons.filter((clubSeason) => clubSeason.club.clubId === homeClubId)
    : meetClubSeasons
  const displayedClubCount = isVirtualMeet
    ? Math.max(localMeetClubSeasons.length, 1)
    : numClubsInMeet

  const lifecycleStepIndex = (() => {
    if (meetStatus == null || meetStatus <= 0) return 0
    if (meetStatus === 1) return 1
    return 2
  })()

  const stepPortion = 100 / 3
  const lifecyclePercent = (lifecycleStepIndex + 1) * stepPortion

  const lifecycleLabel =
    lifecycleStepIndex === 0
      ? "Entries"
      : lifecycleStepIndex === 1
        ? "Heat Sheet"
        : "Results"

  const lifecycleSubLabel =
    lifecycleStepIndex === 0
      ? "In Progress"
      : lifecycleStepIndex === 1
        ? "Meet Ready"
        : "Completed"

  const lifecycleTooltip =
    lifecycleStepIndex === 0
      ? "Entries · in progress"
      : lifecycleStepIndex === 1
        ? "Heat Sheet · meet ready"
        : "Results · published & completed"

  useEffect(() => {
    if (
      !showHostTrigger &&
      !showPoolTrigger &&
      !showVirtualCourseTrigger &&
      !showMyMeetCourseTrigger
    ) {
      return
    }

    const handlePointerDown = (event: PointerEvent) => {
      if (!(event.target instanceof Node)) {
        return
      }

      if (hostAreaRef.current && !hostAreaRef.current.contains(event.target)) {
        setShowHostTrigger(false)
      }

      if (poolAreaRef.current && !poolAreaRef.current.contains(event.target)) {
        setShowPoolTrigger(false)
      }

      if (
        virtualCourseAreaRef.current &&
        !virtualCourseAreaRef.current.contains(event.target)
      ) {
        setShowVirtualCourseTrigger(false)
      }

      if (
        myMeetCourseAreaRef.current &&
        !myMeetCourseAreaRef.current.contains(event.target)
      ) {
        setShowMyMeetCourseTrigger(false)
      }
    }

    document.addEventListener("pointerdown", handlePointerDown)

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown)
    }
  }, [
    showHostTrigger,
    showMyMeetCourseTrigger,
    showPoolTrigger,
    showVirtualCourseTrigger,
  ])

  return (
    <>
      <div className="shadowed" style={{ marginBottom: 16 }}>
        <Row gutter={[8, 8]}>
          <Col xs={24} md={11}>
            <div
              style={{
                fontSize: 12,
                color: "#101242",
                fontFamily:
                  "'Museo Sans Rounded', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
              }}
            >
              {dateTimeOverviewContent || (
                <>
                  <div
                    style={{
                      marginTop: 4,
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <span style={{ marginRight: 6 }}>
                      <FontAwesomeIcon icon={faCalendar} />
                    </span>
                    <span>
                      <span style={{ fontWeight: 400 }}>
                        {isVirtualMeet ? "Meet Window: " : "Meet Date: "}
                      </span>
                      <span style={{ fontWeight: 600 }}>{dateLabel}</span>
                    </span>
                  </div>
                  {formattedMeetTime && (
                    <div
                      style={{
                        marginTop: 4,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <span style={{ marginRight: 6 }}>
                        <FontAwesomeIcon icon={faClock} />
                      </span>
                      <span>
                        <span style={{ fontWeight: 400 }}>Time: </span>{" "}
                        <span style={{ fontWeight: 600 }}>{formattedMeetTime}</span>
                      </span>
                    </div>
                  )}
                </>
              )}
              {isVirtualMeet ? (
                <div style={{ marginTop: 4 }}>
                  <div
                    ref={virtualCourseAreaRef}
                    className={`meet-inline-edit-display${showVirtualCourseTrigger ? " meet-inline-edit-visible" : ""}`}
                    onClick={() => {
                      if (canEditVirtualCourse && !showVirtualCourseTrigger) {
                        setShowVirtualCourseTrigger(true)
                      }
                    }}
                  >
                    <div className="meet-inline-edit-display-row">
                      <span style={{ marginRight: 6 }}>
                        <FontAwesomeIcon icon={faRulerHorizontal} />
                      </span>
                      <span>
                        <span style={{ fontWeight: 400 }}>Virtual Meet Course: </span>
                        <span style={{ fontWeight: 600 }}>
                          {virtualCourseLabel || courseLabel}
                        </span>
                      </span>
                      <Button
                        type="link"
                        size="small"
                        onClick={(event) => {
                          event.stopPropagation()
                          setShowVirtualCourseExplainer(true)
                        }}
                        style={{ paddingInline: 4, height: "auto" }}
                        aria-label="How virtual meet course conversion works"
                      >
                        <InfoCircleOutlined />
                      </Button>
                      {canEditVirtualCourse && onEditVirtualCourse ? (
                        <Button
                          type="link"
                          className="meet-inline-edit-trigger"
                          size="small"
                          onClick={(event) => {
                            event.stopPropagation()
                            onEditVirtualCourse()
                          }}
                          style={{ paddingInline: 0, height: "auto" }}
                          aria-label="Edit virtual meet course"
                        >
                          <FontAwesomeIcon icon={faPenToSquare} />
                        </Button>
                      ) : null}
                    </div>
                  </div>
                  <div
                    ref={myMeetCourseAreaRef}
                    className={`meet-inline-edit-display${showMyMeetCourseTrigger ? " meet-inline-edit-visible" : ""}`}
                    onClick={() => {
                      if (canEditMyMeetCourse && !showMyMeetCourseTrigger) {
                        setShowMyMeetCourseTrigger(true)
                      }
                    }}
                  >
                    <div className="meet-inline-edit-display-row">
                      <span style={{ marginRight: 6 }}>
                        <FontAwesomeIcon icon={faRulerHorizontal} />
                      </span>
                      <span>
                        <span style={{ fontWeight: 400 }}>My Meet Course: </span>
                        <span style={{ fontWeight: 600 }}>
                          {myMeetCourseLabel || courseLabel}
                        </span>
                      </span>
                      {canEditMyMeetCourse && onEditMyMeetCourse ? (
                        <Button
                          type="link"
                          className="meet-inline-edit-trigger"
                          size="small"
                          onClick={(event) => {
                            event.stopPropagation()
                            onEditMyMeetCourse()
                          }}
                          style={{ paddingInline: 0, height: "auto" }}
                          aria-label="Edit my meet course"
                        >
                          <FontAwesomeIcon icon={faPenToSquare} />
                        </Button>
                      ) : null}
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  style={{
                    marginTop: 4,
                  }}
                >
                  <span className="meet-inline-edit-display-row">
                    <span style={{ marginRight: 6 }}>
                      <FontAwesomeIcon icon={faRulerHorizontal} />
                    </span>
                    <span>
                      <span style={{ fontWeight: 400 }}>Course: </span>
                      <span style={{ fontWeight: 600 }}>{courseLabel}</span>
                    </span>
                  </span>
                </div>
              )}
              {(entryLimitsOverviewContent || hasAnyLimitText) && (
                <div
                  style={{
                    marginTop: 4,
                  }}
                >
                  <span className="meet-inline-edit-display-row">
                    <span style={{ marginRight: 6 }}>
                      <FontAwesomeIcon icon={faBadgeSheriff} />
                    </span>
                    <span>
                      {entryLimitsOverviewContent || (
                        <span>
                          <span style={{ fontWeight: 400 }}>Entry limits: </span>
                          {formattedIndividualLimit !== null && (
                            <span>
                              <span style={{ fontWeight: 600 }}>
                                Individual {formattedIndividualLimit}
                              </span>
                            </span>
                          )}
                          {formattedIndividualLimit !== null &&
                            formattedRelayLimit !== null && (
                            <span style={{ marginLeft: 8 }}>· </span>
                          )}
                          {formattedRelayLimit !== null && (
                            <span>
                              <span style={{ fontWeight: 600 }}>
                                Relay {formattedRelayLimit}
                              </span>
                            </span>
                          )}
                          {formattedTotalLimit !== null && (
                            <span>
                              <span style={{ marginLeft: 8 }}>· </span>
                              <span style={{ fontWeight: 600 }}>
                                Total {formattedTotalLimit}
                              </span>
                            </span>
                          )}
                        </span>
                      )}
                    </span>
                  </span>
                </div>
              )}
              {scoringSummaryText && (
                <div style={{ marginTop: 4 }}>
                  <span style={{ fontWeight: 400 }}>Scoring: </span>
                  <span style={{ fontWeight: 600 }}>{scoringSummaryText}</span>
                </div>
              )}
              <div
                style={{
                  marginTop: 4,
                }}
              >
                <span className="meet-inline-edit-display-row">
                  <span style={{ marginRight: 6 }}>
                    <FontAwesomeIcon icon={faUsers} />
                  </span>
                  <span>
                    <span style={{ fontWeight: 400 }}>
                      {isVirtualMeet ? "Clubs in my leg: " : "Clubs in meet: "}
                    </span>
                    <span style={{ fontWeight: 600 }}>{displayedClubCount}</span>
                  </span>
                </span>
              </div>
              {meetEntriesDeadlineContent ? (
                <div style={{ marginTop: 4 }}>{meetEntriesDeadlineContent}</div>
              ) : null}
            </div>
          </Col>
          <Col xs={24} md={7}>
            <div
              style={{
                fontSize: 12,
                color: "#101242",
                fontFamily:
                  "'Museo Sans Rounded', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
              }}
            >
              {!isVirtualMeet && hostClub && (
                <div
                  ref={hostAreaRef}
                  className={`meet-inline-edit-display${showHostTrigger ? " meet-inline-edit-visible" : ""}`}
                  style={{
                    marginBottom: 4,
                  }}
                  onClick={() => {
                    if (canEditHostClub && !showHostTrigger) {
                      setShowHostTrigger(true)
                    }
                  }}
                >
                  <div className="meet-inline-edit-display-row">
                    <span style={{ marginRight: 6 }}>
                      <FontAwesomeIcon icon={faUser} />
                    </span>
                    <span>
                      <span style={{ fontWeight: 400 }}>Host: </span>{" "}
                      <span style={{ fontWeight: 600 }}>
                        {hostClub.nameLong || hostClub.name}
                      </span>
                    </span>
                    {canEditHostClub && onEditHostClub ? (
                      <Button
                        type="link"
                        className="meet-inline-edit-trigger"
                        size="small"
                        onClick={(event) => {
                          event.stopPropagation()
                          onEditHostClub()
                        }}
                        style={{ paddingInline: 0, height: "auto" }}
                        aria-label="Edit host team"
                      >
                        <FontAwesomeIcon icon={faPenToSquare} />
                      </Button>
                    ) : null}
                  </div>
                </div>
              )}
              {meetPool && (
                <div
                  ref={poolAreaRef}
                  className={`meet-inline-edit-display${showPoolTrigger ? " meet-inline-edit-visible" : ""}`}
                  style={{ marginBottom: 4 }}
                  onClick={() => {
                    if (canEditMeetPool && !showPoolTrigger) {
                      setShowPoolTrigger(true)
                    }
                  }}
                >
                  <div className="meet-inline-edit-display-row">
                    <span style={{ marginRight: 6 }}>
                      <FontAwesomeIcon icon={faWaterLadder} />
                    </span>
                    <Tooltip
                      title={
                        meetPool.address
                          ? `${meetPool.pool_name || "Pool"} · ${meetPool.address}`
                          : meetPool.pool_name
                      }
                    >
                      <span>
                        <span style={{ fontWeight: 400 }}>Pool: </span>{" "}
                        <span style={{ fontWeight: 600 }}>
                          {meetPool.pool_name}
                        </span>
                        {meetPool.description ? (
                          <div
                            style={{
                              marginTop: 2,
                              color: "rgba(16, 18, 66, 0.72)",
                              lineHeight: 1.35,
                            }}
                          >
                            {meetPool.description}
                          </div>
                        ) : null}
                        {meetPool.numLanes !== null ? (
                          <div
                            style={{
                              marginTop: meetPool.description ? 4 : 2,
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 6,
                              color: "rgba(16, 18, 66, 0.82)",
                              lineHeight: 1.35,
                            }}
                          >
                            <span style={{ marginTop: 1 }}>
                              <FontAwesomeIcon icon={faRoad} />
                            </span>
                            <span>
                              <span style={{ fontWeight: 400 }}>Lanes: </span>
                              <span style={{ fontWeight: 600 }}>
                                {meetPool.numLanes}
                              </span>
                            </span>
                          </div>
                        ) : null}
                        {(meetPool.address || meetLocation) ? (
                          <div
                            style={{
                              marginTop: 4,
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 6,
                              color: "rgba(16, 18, 66, 0.82)",
                              lineHeight: 1.35,
                            }}
                          >
                            <span style={{ marginTop: 1 }}>
                              <FontAwesomeIcon icon={faLocationDot} />
                            </span>
                            <span>{meetPool.address || meetLocation}</span>
                          </div>
                        ) : null}
                      </span>
                    </Tooltip>
                    {canEditMeetPool && onEditMeetPool ? (
                      seasonEnded ? (
                        <Tooltip title="This season has ended. This action is no longer available.">
                          <Button
                            type="link"
                            size="small"
                            disabled
                            style={{ paddingInline: 0, height: "auto", cursor: "not-allowed" }}
                            aria-label="Editing disabled — season has ended"
                          >
                            <FontAwesomeIcon icon={faPenToSquare} />
                          </Button>
                        </Tooltip>
                      ) : (
                      <Button
                        type="link"
                        className="meet-inline-edit-trigger"
                        size="small"
                        onClick={(event) => {
                          event.stopPropagation()
                          onEditMeetPool()
                        }}
                        style={{ paddingInline: 0, height: "auto" }}
                        aria-label="Edit meet pool"
                      >
                        <FontAwesomeIcon icon={faPenToSquare} />
                      </Button>
                      )
                    ) : null}
                  </div>
                </div>
              )}
              {!meetPool && meetLocation && (
                <div
                  style={{
                    marginTop: 4,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <span style={{ marginRight: 6 }}>
                    <FontAwesomeIcon icon={faLocationDot} />
                  </span>
                  <span>
                    <span style={{ fontWeight: 400 }}>Location: </span>{" "}
                    <span style={{ fontWeight: 600 }}>{meetLocation}</span>
                  </span>
                </div>
              )}
            </div>
          </Col>
          <Col
            xs={24}
            md={6}
            style={{ display: "flex", justifyContent: "center" }}
          >
            <div style={{ textAlign: "center", marginTop: 4 }}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: 0.4,
                  marginBottom: 4,
                }}
              >
                {isVirtualMeet ? "My Leg Status" : "Meet Status"}
              </div>
              <Tooltip title={lifecycleTooltip}>
                <div style={{ display: "inline-block" }}>
                  <Progress
                    type="dashboard"
                    percent={lifecyclePercent}
                    strokeColor={lifecycleStepIndex === 2 ? "#52c41a" : "#1890ff"}
                    railColor="rgba(0, 0, 0, 0.06)"
                    strokeWidth={20}
                    steps={3}
                    format={() => (
                      <div style={{ fontSize: 11, color: "#101242" }}>
                        <div style={{ fontWeight: 600 }}>{lifecycleLabel}</div>
                        <div style={{ fontSize: 10 }}>{lifecycleSubLabel}</div>
                      </div>
                    )}
                  />
                </div>
              </Tooltip>
            </div>
          </Col>
        </Row>
        {deadlineStatusSummaryContent ? (
          <div style={{ marginTop: 12 }}>{deadlineStatusSummaryContent}</div>
        ) : null}
        <div style={{ marginTop: 12 }}>
          <Row gutter={8} style={{ marginBottom: 4 }}>
            <Col style={{ width: 20 }} />
            <Col flex="auto" />
            <Col style={{ width: 320, textAlign: "center" }}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: "#595959",
                }}
              >
                {isVirtualMeet ? "My Club Leg Progress" : "Entries Tracker"}
              </div>
            </Col>
            <Col style={{ width: 80 }} />
          </Row>
          {(() => {
            const hasDivisions = meetDivisions.length > 0

            const renderClubRow = (mCS: IClubSeasonInformation) => {
              const isHome = homeClubId === mCS.club.clubId
              const forceComplete = meetStatus === 2
              const lineupCompleted = forceComplete || Boolean(mCS.confirmLineup)
              const entriesStarted =
                forceComplete ||
                (mCS.entriesCount || 0) > 0 ||
                lineupCompleted

              const steps = [
                { label: "Entries Started", done: entriesStarted },
                { label: "Entries Completed", done: lineupCompleted },
              ] as const

              const firstIncompleteIndex = steps.findIndex((s) => !s.done)
              const activeIndex = firstIncompleteIndex === -1 ? steps.length - 1 : firstIncompleteIndex

              return (
                <Row
                  key={`meet_status_${meetId}_${mCS.club.clubId}`}
                  align="middle"
                  gutter={8}
                  style={{ marginBottom: 2, minHeight: 24 }}
                >
                  <Col
                    style={{
                      width: 20,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: canEditHostClub && !isHome ? "pointer" : "default",
                    }}
                    onMouseEnter={() => { if (canEditHostClub && !isHome) setHoveredHostClubId(mCS.club.clubId!) }}
                    onMouseLeave={() => setHoveredHostClubId(null)}
                    onClick={() => { if (canEditHostClub && !isHome) setPendingHostClub(mCS.club) }}
                  >
                    {isHome ? (
                      <Tooltip title="Host club">
                        <FontAwesomeIcon icon={faHouse} style={{ verticalAlign: "middle" }} />
                      </Tooltip>
                    ) : canEditHostClub && hoveredHostClubId === mCS.club.clubId ? (
                      <Tooltip title="Set as meet host">
                        <FontAwesomeIcon icon={faHouse} style={{ verticalAlign: "middle", opacity: 0.25 }} />
                      </Tooltip>
                    ) : null}
                  </Col>
                  <Col
                    flex="auto"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      minHeight: 20,
                    }}
                  >
                    <Tooltip
                      title={mCS.club.nameLong || mCS.club.name}
                      placement="topLeft"
                    >
                      <span
                        style={{
                          display: "inline-block",
                          maxWidth: "100%",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          lineHeight: 1.2,
                        }}
                      >
                        {mCS.club.nameLong || mCS.club.name}
                      </span>
                    </Tooltip>
                  </Col>
                  <Col style={{ width: 320 }}>
                      <div
                        style={{
                          display: "flex",
                          width: "100%",
                          gap: 4,
                        }}
                      >
                        {steps.map((step, idx) => {
                          const isDone = step.done
                          const isActive = !isDone && idx === activeIndex
                          const heatSheetReadyAction =
                            step.label === "Entries Completed" && !isDone && renderHeatSheetTrackerAction
                              ? renderHeatSheetTrackerAction(mCS)
                              : null

                          const baseStyle: React.CSSProperties = {
                            flex: 1,
                            borderRadius: 999,
                            padding: "2px 6px",
                            fontSize: 11,
                            lineHeight: 1.3,
                            textAlign: "center",
                            whiteSpace: "normal",
                            overflow: "visible",
                            textOverflow: "clip",
                            border: "1px solid transparent",
                          }

                          let background = "#f5f5f5"
                          let color = "#595959"
                          let border = "1px dashed #d9d9d9"
                          let fontWeight: React.CSSProperties["fontWeight"] = 500

                          if (isDone) {
                            background = "#52c41a"
                            color = "#ffffff"
                            border = "1px solid #52c41a"
                            fontWeight = 600
                          } else if (isActive) {
                            background = "#e6f4ff"
                            color = "#0958d9"
                            border = "2px solid #1677ff"
                            fontWeight = 600
                          }

                          return (
                            <Tooltip
                              key={`${mCS.club.clubId}_${step.label}`}
                              title={step.label}
                            >
                              {heatSheetReadyAction ? (
                                <button
                                  type="button"
                                  onClick={heatSheetReadyAction.onClick}
                                  disabled={heatSheetReadyAction.loading}
                                  aria-label="Mark club entries complete"
                                  style={{
                                    ...baseStyle,
                                    background,
                                    color,
                                    border,
                                    fontWeight,
                                    appearance: "none",
                                    WebkitAppearance: "none",
                                    cursor: heatSheetReadyAction.loading ? "wait" : "pointer",
                                    opacity: heatSheetReadyAction.loading ? 0.72 : 1,
                                  }}
                                >
                                  {`${idx + 1}. `}
                                  {step.label}
                                </button>
                              ) : (
                                <div
                                  style={{
                                    ...baseStyle,
                                    background,
                                    color,
                                    border,
                                    fontWeight,
                                  }}
                                >
                                  {isDone ? "✓ " : `${idx + 1}. `}
                                  {step.label}
                                </div>
                              )}
                            </Tooltip>
                          )
                        })}
                      </div>
                    </Col>
                    <Col
                      style={{
                        width: 80,
                        textAlign: "right",
                        fontSize: 11,
                      }}
                    >
                      <div
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "flex-end",
                          gap: 8,
                          flexWrap: "wrap",
                        }}
                      >
                        {entriesStarted && lineupCompleted ? (
                          <span
                            style={{
                              padding: "1px 6px",
                              borderRadius: 999,
                              background: "#f6ffed",
                              color: "#389e0d",
                              border: "1px solid #b7eb8f",
                              fontWeight: 600,
                            }}
                          >
                            Done
                          </span>
                        ) : (
                          <span style={{ color: "#8c8c8c" }}>
                            {`${[
                              entriesStarted,
                              lineupCompleted,
                            ].filter(Boolean).length} of 2`}
                          </span>
                        )}
                      </div>
                    </Col>
                </Row>
              )
            }

            if (!hasDivisions) {
              return localMeetClubSeasons.map(renderClubRow)
            }

            // Division-grouped rendering
            const clubToDivision = new Map<number, string>()
            for (const div of meetDivisions) {
              for (const cId of div.clubIds) clubToDivision.set(cId, div.divisionName)
            }

            const divisionGroups = new Map<string, IClubSeasonInformation[]>()
            for (const div of meetDivisions) divisionGroups.set(div.divisionName, [])
            const unassigned: IClubSeasonInformation[] = []
            for (const mCS of localMeetClubSeasons) {
              const divName = clubToDivision.get(mCS.club.clubId ?? -1)
              if (divName && divisionGroups.has(divName)) {
                divisionGroups.get(divName)!.push(mCS)
              } else {
                unassigned.push(mCS)
              }
            }
            if (unassigned.length > 0) divisionGroups.set("Unassigned", unassigned)

            const nodes: React.ReactNode[] = []
            let groupIdx = 0
            Array.from(divisionGroups.entries()).forEach(([divName, clubs]) => {
              if (clubs.length === 0) return
              nodes.push(
                <div
                  key={`div_header_${divName}`}
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: divName === "Unassigned" ? "#bfbfbf" : "#595959",
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                    marginTop: groupIdx > 0 ? 8 : 2,
                    marginBottom: 2,
                  }}
                >
                  {divName}
                </div>,
              )
              clubs.forEach((mCS: IClubSeasonInformation) => nodes.push(renderClubRow(mCS)))
              groupIdx++
            })
            return nodes
          })()}
        </div>
      </div>
      <Modal
        open={showVirtualCourseExplainer}
        title="How virtual meet courses work"
        onCancel={() => setShowVirtualCourseExplainer(false)}
        footer={null}
      >
        <p>
          Choose the course for the leg of the meet being run at your pool,
          shown here as My Meet Course{myMeetCourseLabel ? ` (${myMeetCourseLabel})` : ""},
          and, if necessary, your times will be converted to the virtual meet
          course{virtualCourseLabel ? ` (${virtualCourseLabel})` : ""} for overall
          virtual meet results.
        </p>
        <p>
          View My Meet Results will show the times in the course actually
          contested at your pool.
        </p>
        <p>
          Only the club that created the virtual meet can update Virtual Meet
          Course. Any user from your team can update My Meet Course.
        </p>
        <p style={{ marginBottom: 0 }}>
          View Virtual Results will appropriately convert times so that all
          results are normalized to the desired virtual meet course.
        </p>
      </Modal>
      <Modal
        open={pendingHostClub !== null}
        title="Change Meet Host"
        onCancel={() => { if (!savingHost) setPendingHostClub(null) }}
        onOk={handleChangeHost}
        okText="Confirm Change"
        confirmLoading={savingHost}
        cancelButtonProps={{ disabled: savingHost }}
      >
        <p style={{ marginBottom: 10 }}>
          You are changing the meet host to{" "}
          <strong>{pendingHostClub?.nameLong || pendingHostClub?.name}</strong> from{" "}
          <strong>{hostClub?.nameLong || hostClub?.name}</strong>.
        </p>
        <p style={{ marginBottom: 0 }}>
          <strong>Important:</strong> the meet host doesn't necessarily host at their own pool, so
          please confirm the pool currently selected is the correct venue for the meet
          {meetPool?.pool_name ? <> (<strong>{meetPool.pool_name}</strong>)</> : ""}.
        </p>
      </Modal>
    </>
  )
}

export default MeetOverviewTab
